import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAdminAuditLog } from "@/lib/admin.users.functions";

export const Route = createFileRoute("/admin/audit")({ component: AuditPage });

function DetailsCell({ details }: { details: string | null }) {
  if (!details) return <span className="text-ink-tertiary text-xs">—</span>;
  let pretty = details;
  try {
    pretty = JSON.stringify(JSON.parse(details), null, 2);
  } catch {
    /* keep raw */
  }
  return (
    <details className="group">
      <summary className="cursor-pointer text-xs text-ink-tertiary hover:text-ink select-none">
        <span className="group-open:hidden">Show details</span>
        <span className="hidden group-open:inline">Hide details</span>
      </summary>
      <pre className="mt-2 p-2 bg-muted/40 border border-border text-[11px] font-mono whitespace-pre-wrap break-all max-h-72 overflow-auto">
        {pretty}
      </pre>
    </details>
  );
}

function AuditPage() {
  const fn = useServerFn(listAdminAuditLog);
  const [page, setPage] = useState(0);
  const pageSize = 100;
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "audit", page],
    queryFn: () => fn({ data: { limit: pageSize, offset: page * pageSize } }),
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-2">Audit</p>
        <h2 className="font-serif text-2xl text-primary mb-4">Admin activity log</h2>
        <p className="text-sm text-ink-secondary mb-6 max-w-2xl">
          Every admin write — role grants, user edits, institution approvals
          and rejections — is recorded here.
        </p>
      </div>

      {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
      {error && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load audit log"}
        </p>
      )}

      {data && (
        <>
          {/* Desktop / wide screens: table */}
          <div className="hidden md:block border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className="w-[160px]" />
                <col className="w-[220px]" />
                <col className="w-[180px]" />
                <col className="w-[220px]" />
                <col />
              </colgroup>
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-ink-tertiary">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Admin</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Target</th>
                  <th className="text-left p-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-border align-top">
                    <td className="p-3 text-xs text-ink-tertiary whitespace-nowrap">
                      {new Date(e.created_at).toLocaleString("en-GB")}
                    </td>
                    <td className="p-3 font-mono text-xs break-all">{e.admin_email ?? "—"}</td>
                    <td className="p-3 font-mono text-xs break-all">{e.action}</td>
                    <td className="p-3 font-mono text-xs break-all">
                      {e.target_email ?? e.target_user_id ?? "—"}
                    </td>
                    <td className="p-3">
                      <DetailsCell details={e.details} />
                    </td>
                  </tr>
                ))}
                {data.entries.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-ink-tertiary text-sm">
                      No admin actions logged yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <div className="md:hidden space-y-3">
            {data.entries.map((e) => (
              <div key={e.id} className="border border-border bg-card p-3 space-y-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-mono text-xs break-all">{e.action}</span>
                  <span className="text-[10px] text-ink-tertiary whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString("en-GB")}
                  </span>
                </div>
                <dl className="grid grid-cols-[64px_1fr] gap-x-2 gap-y-1 text-xs">
                  <dt className="text-ink-tertiary uppercase tracking-wide">Admin</dt>
                  <dd className="font-mono break-all">{e.admin_email ?? "—"}</dd>
                  <dt className="text-ink-tertiary uppercase tracking-wide">Target</dt>
                  <dd className="font-mono break-all">
                    {e.target_email ?? e.target_user_id ?? "—"}
                  </dd>
                </dl>
                <DetailsCell details={e.details} />
              </div>
            ))}
            {data.entries.length === 0 && (
              <p className="text-center text-ink-tertiary text-sm p-6 border border-border bg-card">
                No admin actions logged yet.
              </p>
            )}
          </div>

          {data.total > pageSize && (
            <div className="flex items-center justify-between text-xs font-mono text-ink-tertiary">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 border border-border disabled:opacity-40"
              >
                ← Prev
              </button>
              <span>
                Page {page + 1} of {Math.max(1, Math.ceil(data.total / pageSize))} ·{" "}
                {data.total} total
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * pageSize >= data.total}
                className="px-3 py-1.5 border border-border disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
